import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  FaCheckCircle, 
  FaCalendarAlt, 
  FaClock, 
  FaUser, 
  FaMapMarkerAlt, 
  FaVideo, 
  FaEnvelope,
  FaArrowLeft,
  FaDownload,
  FaShare
} from 'react-icons/fa';

interface Session {
  _id: string;
  teacherId: {
    basicInfo: {
      fullName: string;
      country: string;
      profilePicture?: string;
    };
  };
  scheduledDate: string;
  duration: number;
  sessionType: string;
  meetingLink?: string;
  notes?: {
    studentNotes?: string;
  };
  payment: {
    amount: number;
    currency: string;
    status: string;
  };
}

const SessionSuccess: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchSession();
  }, [sessionId, user, navigate]);

  const fetchSession = async () => {
    try {
      setLoading(true);
      
      // Mock session data
      const mockSession: Session = {
        _id: sessionId || '',
        teacherId: {
          basicInfo: {
            fullName: 'Bahar Erfanian',
            country: 'United States',
            profilePicture: '/images/profiles/bahar-erfanian.jpg'
          }
        },
        scheduledDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Tomorrow
        duration: 60,
        sessionType: 'regular',
        meetingLink: 'https://meet.google.com/abc-defg-hij',
        notes: {
          studentNotes: 'Looking forward to learning Persian!'
        },
        payment: {
          amount: 28,
          currency: 'USD',
          status: 'paid'
        }
      };

      setSession(mockSession);
    } catch (error) {
      console.error('Error fetching session:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const addToCalendar = () => {
    if (!session) return;
    
    const startDate = new Date(session.scheduledDate);
    const endDate = new Date(startDate.getTime() + session.duration * 60000);
    
    const calendarEvent = {
      title: `Persian Lesson with ${session.teacherId.basicInfo.fullName}`,
      description: `Persian language lesson session${session.notes?.studentNotes ? `\n\nNotes: ${session.notes.studentNotes}` : ''}`,
      location: 'Online (Zoom)',
      startTime: startDate.toISOString(),
      endTime: endDate.toISOString()
    };

    // Create calendar URL
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(calendarEvent.title)}&details=${encodeURIComponent(calendarEvent.description)}&location=${encodeURIComponent(calendarEvent.location)}&dates=${startDate.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')}/${endDate.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')}`;
    
    window.open(googleCalendarUrl, '_blank');
  };

  const shareSession = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Persian Lesson Session',
        text: `I just booked a Persian lesson with ${session?.teacherId.basicInfo.fullName}!`,
        url: window.location.href
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Session link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading session details...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Session not found</h3>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <FaArrowLeft className="mr-2" />
              Back to Dashboard
            </button>
            <h1 className="text-xl font-semibold text-gray-900">Booking Confirmed</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Success Message */}
        <div className="bg-white rounded-2xl shadow-sm border p-8 mb-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaCheckCircle className="text-green-500 text-4xl" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Session Booked Successfully!
            </h2>
            <p className="text-gray-600 text-lg">
              Your Persian lesson session has been confirmed. You'll receive a confirmation email shortly.
            </p>
          </div>

          {/* Session Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Session Details</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <FaCalendarAlt className="text-blue-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Date</p>
                    <p className="font-medium">{formatDate(session.scheduledDate)}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <FaClock className="text-blue-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Time</p>
                    <p className="font-medium">{formatTime(session.scheduledDate)}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <FaClock className="text-blue-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Duration</p>
                    <p className="font-medium">{session.duration} minutes</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <FaUser className="text-blue-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Session Type</p>
                    <p className="font-medium capitalize">{session.sessionType}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Teacher Information</h3>
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-lg font-bold mr-3">
                  {session.teacherId.basicInfo.profilePicture ? (
                    <img
                      src={session.teacherId.basicInfo.profilePicture}
                      alt={session.teacherId.basicInfo.fullName}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    session.teacherId.basicInfo.fullName.charAt(0)
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {session.teacherId.basicInfo.fullName}
                  </p>
                  <div className="flex items-center text-sm text-gray-600">
                    <FaMapMarkerAlt className="mr-1" />
                    {session.teacherId.basicInfo.country}
                  </div>
                </div>
              </div>
              {session.notes?.studentNotes && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Your Notes:</p>
                  <p className="text-sm text-gray-900 bg-white p-3 rounded border">
                    {session.notes.studentNotes}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Meeting Link */}
          {session.meetingLink && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FaVideo className="text-blue-500 mr-2" />
                Meeting Link
              </h3>
              <p className="text-gray-600 mb-4">
                Your meeting link will be available 15 minutes before the session starts.
              </p>
              <a
                href={session.meetingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <FaVideo className="mr-2" />
                Join Meeting
              </a>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={addToCalendar}
              className="flex-1 flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FaCalendarAlt className="mr-2" />
              Add to Calendar
            </button>
            <button
              onClick={shareSession}
              className="flex-1 flex items-center justify-center px-6 py-3 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <FaShare className="mr-2" />
              Share Session
            </button>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-white rounded-2xl shadow-sm border p-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">What's Next?</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-blue-600 font-semibold text-sm">1</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Check Your Email</h4>
                <p className="text-sm text-gray-600">
                  You'll receive a confirmation email with all the session details and meeting link.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-blue-600 font-semibold text-sm">2</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Prepare for Your Session</h4>
                <p className="text-sm text-gray-600">
                  Review any materials or topics you'd like to discuss with your teacher.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-blue-600 font-semibold text-sm">3</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Join on Time</h4>
                <p className="text-sm text-gray-600">
                  Be ready to join the meeting 5 minutes before your scheduled time.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-blue-600 font-semibold text-sm">4</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Enjoy Your Lesson</h4>
                <p className="text-sm text-gray-600">
                  Have a great learning experience with your Persian teacher!
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Actions */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex-1 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Go to Dashboard
          </button>
          <button
            onClick={() => navigate('/teachers')}
            className="flex-1 px-6 py-3 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
          >
            Book Another Session
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionSuccess;
